<script setup lang="ts">
import { computed, watch } from 'vue'
import { bbox } from '@turf/turf'
import { MapSource, MapLayer } from '@/components/map'
import { hasRealGeometry } from '@/utils/mapbox/staticMap'
import type { PendingLayer } from './types'

// Draws the selected card's AOI onto the app's existing map instance (the admin
// page is a panel beside it) and keeps it in view.

const props = defineProps<{
  map: any
  layers: PendingLayer[]
  selectedCode: string | null
}>()

const SOURCE_ID = 'admin-aoi'
const COLOR = '#2563eb'

const selectedFeature = computed(() => {
  const layer = props.layers.find(l => l.code === props.selectedCode)
  if (!layer?.geometry || !hasRealGeometry({ geometry: layer.geometry })) return null
  return {
    type: 'Feature',
    properties: { code: layer.code, name: layer.layer_name },
    geometry: layer.geometry,
  }
})

const collection = computed(() => ({
  type: 'FeatureCollection',
  features: selectedFeature.value ? [selectedFeature.value] : [],
}))

const fitTo = (target: any) => {
  if (!props.map || !target) return
  try {
    props.map.fitBounds(bbox(target as any), { padding: 80, maxZoom: 13, duration: 500 })
  } catch (e) { /* empty geometry */ }
}

const zoomTo = (code: string) => {
  if (selectedFeature.value?.properties.code === code) fitTo(selectedFeature.value)
}

defineExpose({ zoomTo })

watch(() => props.selectedCode, () => fitTo(selectedFeature.value))

const onSourceLoaded = () => fitTo(selectedFeature.value)
</script>

<template>
  <MapSource :map="map" :id="SOURCE_ID" type="geojson" :data="collection">
    <MapLayer
      :map="map"
      :id="`${SOURCE_ID}-fill`"
      type="fill"
      :source="SOURCE_ID"
      :options="{ paint: { 'fill-color': COLOR, 'fill-opacity': 0.2 }, filter: ['==', '$type', 'Polygon'] }"
      :interactive="false"
      @loaded="onSourceLoaded"
    />
    <MapLayer
      :map="map"
      :id="`${SOURCE_ID}-line`"
      type="line"
      :source="SOURCE_ID"
      :options="{ paint: { 'line-color': COLOR, 'line-width': 2.5 }, filter: ['!=', '$type', 'Point'] }"
      :interactive="false"
    />
    <MapLayer
      :map="map"
      :id="`${SOURCE_ID}-point`"
      type="circle"
      :source="SOURCE_ID"
      :options="{ paint: { 'circle-color': COLOR, 'circle-radius': 7, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 2 }, filter: ['==', '$type', 'Point'] }"
      :interactive="false"
    />
  </MapSource>
</template>
